<template>
  <div class="dq-dt-scroll" ref="scroll" @scroll="onScroll">
    <!-- Inner table: min-width = sum of all column widths, grows to fill wider containers -->
    <div class="dq-dt" :style="{ minWidth: minWidth + 'px' }" role="table">

      <!-- Sticky header row -->
      <div class="dq-dt-head" role="row">
        <!-- select-all checkbox -->
        <div v-if="selectable" class="dq-dt-cell dq-dt-cell--fixed dq-dt-cell--checkbox" :style="fixedStyle(checkboxWidth)">
          <CheckBox :modelValue="allSelected" :indeterminate="someSelected" thickness="1px" @update:modelValue="toggleSelectAll" />
        </div>

        <!-- data headers -->
        <div v-for="col in columns" :key="col.key" class="dq-dt-cell dq-dt-th" :style="cellStyle(col)"
          :class="[alignClass(col), { 'dq-dt-th--sortable': col.sortable }]" role="columnheader"
          @click="col.sortable && onSort(col.key)">
          <slot :name="`header-${col.key}`" :column="col">
            <span class="dq-dt-th-label">{{ col.label }}</span>
          </slot>
          <span v-if="col.sortable && sortKey === col.key" class="dq-dt-sort-arrow">{{ sortDir === 'asc' ? '▲' : '▼' }}</span>
        </div>

        <!-- actions header (empty spacer) -->
        <div v-if="hasActions" class="dq-dt-cell dq-dt-cell--fixed" :style="fixedStyle(actionsWidth)" role="columnheader"></div>
      </div>

      <!-- Body rows -->
      <div class="dq-dt-body" role="rowgroup">
        <div v-for="row in rows" :key="row[rowKey]" class="dq-dt-row"
          :class="{ 'dq-dt-row--clickable': clickableRows, 'dq-dt-row--selected': selectable && isSelected(row) }"
          role="row" @click="onRowClick(row, $event)">

          <!-- row checkbox -->
          <div v-if="selectable" class="dq-dt-cell dq-dt-cell--fixed dq-dt-cell--checkbox" :style="fixedStyle(checkboxWidth)" @click.stop>
            <CheckBox :modelValue="isSelected(row)" thickness="1px" @update:modelValue="toggleRow(row)" />
          </div>

          <!-- data cells -->
          <div v-for="col in columns" :key="col.key" class="dq-dt-cell" :style="cellStyle(col)" :class="alignClass(col)" role="cell">
            <slot :name="`cell-${col.key}`" :row="row" :value="getValue(row, col)" :column="col">
              <span class="dq-dt-text">{{ getValue(row, col) }}</span>
            </slot>
          </div>

          <!-- actions cell -->
          <div v-if="hasActions" class="dq-dt-cell dq-dt-cell--fixed dq-dt-cell--actions" :style="fixedStyle(actionsWidth)" @click.stop>
            <button class="dq-dt-actions-trigger" :class="{ 'is-open': openRowId === row[rowKey] }" @click="toggleActions(row, $event)">
              <vue-feather type="more-horizontal" size="18" />
            </button>
          </div>
        </div>
      </div>

      <!-- loading / empty -->
      <div v-if="loading" class="loading-status">
        <img class="loading-icon" src="/loading.svg" alt="Loading..." />
      </div>
      <div v-else-if="!rows.length" class="dq-dt-empty">
        <slot name="empty"><span class="no-value">No results</span></slot>
      </div>

      <!-- Sticky footer row (totals / counts) — opt-in via footer-<key> slots -->
      <div v-if="hasFooter && !loading && rows.length" class="dq-dt-foot" role="row">
        <!-- checkbox spacer -->
        <div v-if="selectable" class="dq-dt-cell dq-dt-cell--fixed dq-dt-cell--checkbox" :style="fixedStyle(checkboxWidth)"></div>

        <!-- footer cells -->
        <div v-for="col in columns" :key="col.key" class="dq-dt-cell dq-dt-tf" :style="cellStyle(col)" :class="alignClass(col)" role="cell">
          <slot :name="`footer-${col.key}`" :column="col" :rows="rows"></slot>
        </div>

        <!-- actions spacer -->
        <div v-if="hasActions" class="dq-dt-cell dq-dt-cell--fixed" :style="fixedStyle(actionsWidth)" role="cell"></div>
      </div>
    </div>

    <!-- Single shared floating menu for the actions column -->
    <FloatingDropdown ref="actionsDropdown" :open="!!openRowId" :trigger-ref="openTriggerEl" :match-width="false" align="right" @dismiss="closeActions">
      <div class="dq-dt-menu">
        <slot name="row-actions" :row="openRow" :close="closeActions" />
      </div>
    </FloatingDropdown>
  </div>
</template>

<script>
import VueFeather from 'vue-feather'
import CheckBox from '@/design/forms/CheckBox.vue'
import FloatingDropdown from '@/design/floating/FloatingDropdown.vue'

export default {
  name: 'DataTable',
  components: {
    'vue-feather': VueFeather,
    CheckBox,
    FloatingDropdown
  },
  props: {
    // [{ key, label, baseWidth, align?: 'left'|'center'|'right', sortable?: bool, fixed?: bool }]
    // fixed: stays at baseWidth instead of taking a share of the leftover space
    columns: {
      type: Array,
      required: true,
      validator: (cols) => cols.every(c => c.key && typeof c.baseWidth === 'number')
    },
    rows: { type: Array, default: () => [] },
    rowKey: { type: String, default: 'id' },

    // selection (v-model:selectedIds)
    selectable: { type: Boolean, default: false },
    selectedIds: { type: Array, default: () => [] },
    checkboxWidth: { type: Number, default: 34 },

    // actions column — null = auto-detect from the row-actions slot
    showActions: { type: Boolean, default: null },
    actionsWidth: { type: Number, default: 56 },

    // footer / totals row — null = auto-detect from any footer-<key> slot
    showFooter: { type: Boolean, default: null },

    // row interaction
    clickableRows: { type: Boolean, default: false },

    // sorting (controlled — parent owns the sorted rows)
    sortKey: { type: String, default: '' },
    sortDir: { type: String, default: 'desc', validator: (v) => ['asc', 'desc'].includes(v) },

    // state
    loading: { type: Boolean, default: false },

    // infinite scroll
    infiniteScroll: { type: Boolean, default: false },
    doneLoading: { type: Boolean, default: true }
  },
  emits: ['update:selectedIds', 'row-click', 'sort', 'load-more'],
  data() {
    return {
      openRowId: null,
      openTriggerEl: null
    }
  },
  computed: {
    hasActions() {
      return this.showActions !== null ? this.showActions : !!this.$slots['row-actions']
    },
    hasFooter() {
      if (this.showFooter !== null) return this.showFooter
      return Object.keys(this.$slots).some(name => name.startsWith('footer-'))
    },
    minWidth() {
      const data = this.columns.reduce((sum, c) => sum + (c.baseWidth || 0), 0)
      return data + (this.selectable ? this.checkboxWidth : 0) + (this.hasActions ? this.actionsWidth : 0)
    },
    selectedSet() {
      return new Set(this.selectedIds)
    },
    allSelected() {
      return this.rows.length > 0 && this.rows.every(r => this.selectedSet.has(r[this.rowKey]))
    },
    someSelected() {
      return this.selectedIds.length > 0 && !this.allSelected
    },
    openRow() {
      return this.rows.find(r => r[this.rowKey] === this.openRowId) || null
    }
  },
  mounted() {
    window.addEventListener('click', this.handleOutsideClick)
  },
  unmounted() {
    window.removeEventListener('click', this.handleOutsideClick)
  },
  methods: {
    cellStyle(col) {
      if (col.fixed) return this.fixedStyle(col.baseWidth)
      // grow factor == basis => leftover space distributes proportionally to base width
      return { flex: `${col.baseWidth} 0 ${col.baseWidth}px` }
    },
    fixedStyle(width) {
      return { flex: `0 0 ${width}px` }
    },
    alignClass(col) {
      if (col.align === 'right') return 'dq-dt-cell--right'
      if (col.align === 'center') return 'dq-dt-cell--center'
      return ''
    },
    getValue(row, col) {
      return row[col.key]
    },
    onSort(key) {
      this.$emit('sort', key)
    },
    onRowClick(row, event) {
      if (this.clickableRows) this.$emit('row-click', row, event)
    },
    onScroll(event) {
      if (!this.infiniteScroll || this.loading || this.doneLoading) return
      const el = event.target
      const max = el.scrollHeight - el.offsetHeight
      if (max <= 0) return
      if (el.scrollTop / max >= 0.95) this.$emit('load-more')
    },

    // selection — controlled, order-preserving emit
    isSelected(row) {
      return this.selectedSet.has(row[this.rowKey])
    },
    toggleRow(row) {
      const id = row[this.rowKey]
      const next = this.selectedSet.has(id)
        ? this.selectedIds.filter(x => x !== id)
        : [...this.selectedIds, id]
      this.$emit('update:selectedIds', next)
    },
    toggleSelectAll() {
      const next = this.allSelected ? [] : this.rows.map(r => r[this.rowKey])
      this.$emit('update:selectedIds', next)
    },

    // actions menu
    toggleActions(row, event) {
      event.stopPropagation()
      const id = row[this.rowKey]
      if (this.openRowId === id) {
        this.closeActions()
      } else {
        this.openRowId = id
        this.openTriggerEl = event.currentTarget
      }
    },
    closeActions() {
      this.openRowId = null
      this.openTriggerEl = null
    },
    handleOutsideClick(event) {
      if (!this.openRowId) return
      if (this.$refs.actionsDropdown && this.$refs.actionsDropdown.containsElement(event.target)) return
      this.closeActions()
    }
  }
}
</script>

<style scoped>
.dq-dt-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  position: relative;
}

.dq-dt {
  width: 100%;
  /* min-width applied inline = sum of column base widths */
}

/* rows */
.dq-dt-head,
.dq-dt-row {
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid #DDD;
}

.dq-dt-head {
  position: sticky;
  top: 0;
  z-index: 10;
  height: 36px;
  background: #FFF;
}

.dq-dt-row {
  height: 40px;
}

.dq-dt-row--clickable {
  cursor: pointer;
}

.dq-dt-row:hover {
  background: #f5f6f8;
}

.dq-dt-row--selected {
  background: #f3f0ff;
}

/* cells */
.dq-dt-cell {
  box-sizing: border-box;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  overflow: hidden;
}

.dq-dt-cell--right {
  justify-content: flex-end;
  text-align: right;
}

.dq-dt-cell--center {
  justify-content: center;
  text-align: center;
}

.dq-dt-cell--fixed {
  flex-grow: 0;
  flex-shrink: 0;
}

.dq-dt-cell--checkbox {
  /* hug the value column: roomy on the left, tight on the right */
  justify-content: flex-end;
  padding: 0 2px 0 10px;
}

/* header look — mirrors .data-table th */
.dq-dt-th {
  text-transform: uppercase;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #999;
  letter-spacing: 0.03em;
  white-space: nowrap;
  user-select: none;
}

.dq-dt-th-label {
  overflow: hidden;
  text-overflow: ellipsis;
}

.dq-dt-th--sortable {
  cursor: pointer;
}

.dq-dt-th--sortable:hover {
  color: #333;
}

.dq-dt-sort-arrow {
  font-size: 0.625rem;
  margin-left: 4px;
  color: #333;
  flex-shrink: 0;
}

/* actions trigger (3 dots) */
.dq-dt-cell--actions {
  justify-content: center;
}

.dq-dt-actions-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  border-radius: 4px;
  color: #374151;
  cursor: pointer;
  opacity: 0.4;
  transition: opacity 0.15s ease, background 0.15s ease;
}

.dq-dt-row:hover .dq-dt-actions-trigger {
  opacity: 1;
}

.dq-dt-actions-trigger:hover,
.dq-dt-actions-trigger.is-open {
  background: #f3f4f6;
  opacity: 1;
}

.dq-dt-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 12px;
}

/* footer / totals row — sticks to the bottom of the scroll viewport */
.dq-dt-foot {
  display: flex;
  align-items: stretch;
  position: sticky;
  bottom: 0;
  z-index: 10;
  height: 36px;
  background: #F9F9F9;
  border-top: 1px solid #DDD;
}

.dq-dt-tf {
  font-size: 0.875rem;
  font-weight: 400;
  color: #6b7280;
  white-space: nowrap;
}
</style>

<style>
/* Not scoped — these apply to slot content (rendered in the parent's scope)
   and to the menu teleported to <body> by FloatingDropdown. */

/* truncating text cell — usable from consumer cell- slots */
.dq-dt-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

/* null / empty value — muted italic; usable from consumer cell- slots and the empty state.
   Scoped under .dq-dt so it overrides the global theme.css .no-value only inside a table. */
.dq-dt .no-value {
  color: #b3b3b3;
  font-style: italic;
}

/* footer-slot helpers — counts and totals share one consistent style */
.dq-dt-foot-count {
  color: #6b7280;
  font-weight: 400;
}

.dq-dt-foot-total {
  color: #6b7280;
  font-weight: 400;
}

.dq-dt-menu {
  background: #FFF;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.05);
  border-radius: 6px;
  min-width: 160px;
  padding: 4px;
  display: flex;
  flex-direction: column;
}

.dq-dt-menu-item {
  padding: 10px 12px;
  border-radius: 4px;
  font-size: 1rem;
  color: #374151;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.1s ease;
}

.dq-dt-menu-item:hover {
  background: #f3f4f6;
}

.dq-dt-menu-item--danger {
  color: #dc2626;
}

.dq-dt-menu-item--danger:hover {
  background: #fef2f2;
}

.dq-dt-menu-item--disabled {
  color: #9ca3af;
  cursor: not-allowed;
}

.dq-dt-menu-item--disabled:hover {
  background: transparent;
}
</style>
